'use client';

import { useMemo } from 'react';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import { PerpPosition, PositionFlag } from '@velocity-exchange/sdk';
import { isIsolatedPositionBeingLiquidated } from 'src/utils/dev/devLiquidationOverrides';
import { getSubscribedClient } from 'src/utils/account';

export interface BeingLiquidatedPosition {
	userKey: string;
	position: PerpPosition;
}

/**
 * Collects all positions across the user's subaccounts that have the
 * BEING_LIQUIDATED flag set (isolated margin positions under liquidation).
 */
const usePositionsBeingLiquidated = (): BeingLiquidatedPosition[] => {
	const accounts = useTickedAllSubAccounts();

	const positions = useMemo(() => {
		if (!accounts || accounts.length === 0) return [];

		const result: BeingLiquidatedPosition[] = [];

		for (const acct of accounts) {
			const userClient = getSubscribedClient(acct);
			const ua = userClient?.getUserAccount();
			if (!ua || !userClient) continue;

			for (const perpPos of ua.perpPositions) {
				// Check both on-chain flag and dev override
				const isBeingLiquidatedOnChain =
					(perpPos.positionFlag &
						(PositionFlag.BeingLiquidated | PositionFlag.Bankruptcy)) >
					0;
				const isBeingLiquidatedDev = isIsolatedPositionBeingLiquidated(
					userClient,
					perpPos.marketIndex,
					acct.userKey
				);
				if (isBeingLiquidatedOnChain || isBeingLiquidatedDev) {
					result.push({
						userKey: acct.userKey,
						position: perpPos,
					});
				}
			}
		}

		return result;
	}, [accounts]);

	return positions;
};

export default usePositionsBeingLiquidated;
